import { ProfileSectionTitleContext } from './profileSectionTitle';
import type { ReactNode } from 'react';

/**
 * The sidebar profile layout — identity and navigation on the left, one panel of content on
 * the right.
 *
 * This is the first of the product's two canonical profile shapes. Use it when a record has
 * more about it than one screen holds, so the content splits into panels the reader picks
 * between: an account, a person, a case file. The other shape — header on top, content
 * below — suits a record whose content is a single continuous thing, typically one table.
 *
 * Both columns scroll independently. The page itself never scrolls: the shell is
 * height-capped and each side owns its own overflow, so the identity block stays put while
 * a long panel scrolls beside it.
 *
 * DELIBERATE DEVIATION from reference §7, recorded in CLAUDE.md. §7 describes a single
 * stacked layout (breadcrumb → identity banner → toolbar → status banner → content); the
 * product uses these two shapes instead.
 */
export function ProfileShell({
  header,
  sectionTitle,
  sidebar,
  banner,
  children,
  id,
}: {
  /**
   * A full-width identity strip above both columns — `<ProfileHeader>`.
   *
   * **Optional, and it MOVES the identity rather than repeating it.** A header carrying the
   * avatar, the name and the status beside a sidebar carrying the same three is one record
   * printed twice on one screen. A page that passes this drops `<ProfileIdentity>` and
   * leaves the sidebar to the panel picker.
   */
  header?: ReactNode;
  /**
   * The open panel’s name, which every `<ProfileSection>` beneath this shell heads itself
   * with. Resolve it from the nav with `findNavLabel` rather than typing it: the rail and
   * the heading are one fact, and a second copy is one a rename leaves behind.
   */
  sectionTitle?: string;
  /** The left column: identity block (or, with a header, just the nav) and actions. */
  sidebar: ReactNode;
  /**
   * Record-level state, pinned above the panel content and shown on every panel — a lockout
   * or a suspension is true of the whole record, so it must not be something the reader
   * only discovers by opening the right tab.
   */
  banner?: ReactNode;
  /** The right column: the selected panel. */
  children: ReactNode;
  id?: string;
}) {
  return (
    <ProfileSectionTitleContext.Provider value={sectionTitle}>
    <div id={id} className="flex h-full min-h-0 min-w-0 flex-col bg-white">
      {header}

      {/* The two columns, under whatever the header took. `min-h-0` again, or the row
          grows past the shell instead of scrolling inside it. */}
      <div className="flex min-h-0 min-w-0 flex-1">
        <aside
          aria-label="Record"
          className="modern-scrollbar flex w-72 shrink-0 flex-col overflow-y-auto border-r border-slate-200"
        >
          {sidebar}
        </aside>

        {/* min-w-0 is load-bearing: without it a wide panel pushes the sidebar off-screen
          instead of scrolling inside its own container. */}
        <main className="modern-scrollbar min-h-0 min-w-0 flex-1 overflow-y-auto bg-slate-50/60">
          {/*
          Cards are capped to a readable measure and centred rather than stretched to the
          viewport. A label/value table run out to 1600px puts its value column a screen away
          from its label, and the eye loses the row on the way across.
        */}
          <div className="mx-auto flex min-w-0 max-w-4xl flex-col gap-5 p-5">
            {banner}
            {children}
          </div>
        </main>
      </div>
    </div>
    </ProfileSectionTitleContext.Provider>
  );
}
